import React, { useState } from 'react';
import {
  FiChevronRight,
  FiDownload,
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

import { TopBar } from '@/design-system/components/organisms/TopBar';
import { DocumentModal } from '@/features/onboarding/components/DocumentModal';
import { NewApontamentoModal, type NewApontamentoData } from '@/features/onboarding/components/NewApontamentoModal';
import { Sidebar } from '@/features/onboarding/components/Sidebar';
import { ONBOARDING_SIDEBAR_ITEMS } from '@/shared/constants/sidebar.constants';

export const ApprovedClientApontamentosPage: React.FC = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState({ title: '', url: '' });
  const [isNewApontamentoModalOpen, setIsNewApontamentoModalOpen] = useState(false);

  const handleOpenDocument = (title: string, url?: string) => {
    setCurrentDocument({ title, url: url || '' });
    setIsDocumentModalOpen(true);
  };

  const handleSaveApontamento = (data: NewApontamentoData) => {
    // Aqui você faria a chamada API para salvar o apontamento
    setIsNewApontamentoModalOpen(false);
  };

  const apontamentos = [
    {
      id: 1,
      tipo: 'Tipo de apontamento',
      tipoColor: 'purple',
      data: '26/10/2025 às 10h30',
      autor: 'Camila Oliveira',
      descricao: 'Encontrado informações divergentes, será necessário uma análise mais profunda.',
      documento: true,
    },
    {
      id: 2,
      tipo: 'Onboarding reprovado',
      tipoColor: 'red',
      data: '26/10/2025 às 10h30',
      autor: 'Camila Oliveira',
      descricao: 'Análise concluída e aprovada.',
      documento: false,
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      <TopBar />

      <div className="flex">
        {/* Sidebar */}
        <Sidebar items={ONBOARDING_SIDEBAR_ITEMS} className="w-64 min-h-[calc(100vh-140px)]" />

        {/* Main Content */}
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-sm text-neutral-600 mb-6">
              <button
                type="button"
                onClick={() => navigate('/onboarding/approved')}
                className="hover:text-purple-600 transition-colors"
              >
                Aprovados
              </button>
              <FiChevronRight className="w-4 h-4" />
              <button
                type="button"
                onClick={() => navigate(`/onboarding/approved/client/${clientId ?? ''}`)}
                className="hover:text-purple-600 transition-colors"
              >
                Cliente
              </button>
              <FiChevronRight className="w-4 h-4" />
              <span className="text-neutral-800 font-medium">Apontamentos</span>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <h1 className="text-3xl font-bold text-neutral-800">FIDUCIA SCM</h1>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                  Formal
                </span>
              </div>
            </div>

            {/* Histórico de apontamentos */}
            <div className="bg-white rounded-lg border border-neutral-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-neutral-800 mb-6">Histórico de apontamentos</h2>

              <div className="space-y-4">
                {apontamentos.map((apontamento) => (
                  <div
                    key={apontamento.id}
                    className="border border-neutral-200 rounded-lg p-4"
                  >
                    {/* Badge */}
                    <div className="mb-3">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                          apontamento.tipoColor === 'purple'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {apontamento.tipo}
                      </span>
                    </div>

                    {/* Data e Autor */}
                    <p className="text-sm font-semibold text-neutral-800 mb-2">
                      {apontamento.data} - {apontamento.autor}
                    </p>

                    {/* Descrição */}
                    <p className="text-sm text-neutral-600 mb-3">{apontamento.descricao}</p>

                    {/* Documento (se existir) */}
                    {apontamento.documento && (
                      <button
                        type="button"
                        onClick={() => { handleOpenDocument('Documento complementar'); }}
                        className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-purple-600 rounded-lg text-sm font-medium text-neutral-700 hover:bg-purple-50 transition-colors"
                      >
                        <FiDownload className="w-4 h-4" />
                        Documento complementar
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => navigate(`/onboarding/approved/client/${clientId ?? ''}`)}
                className="px-6 py-3 bg-white border-2 border-purple-600 text-neutral-700 rounded-lg font-medium hover:bg-purple-50 transition-colors"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={() => { setIsNewApontamentoModalOpen(true); }}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                Novo apontamento
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* Document Modal */}
      <DocumentModal
        isOpen={isDocumentModalOpen}
        onClose={() => { setIsDocumentModalOpen(false); }}
        documentTitle={currentDocument.title}
        documentUrl={currentDocument.url}
      />

      {/* New Apontamento Modal */}
      <NewApontamentoModal
        isOpen={isNewApontamentoModalOpen}
        onClose={() => { setIsNewApontamentoModalOpen(false); }}
        onSave={handleSaveApontamento}
      />
    </div>
  );
};
